import { useEffect } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import {
  api,
  ApiError,
  type CourseValues,
  type EventSummary,
  type GoverningValues,
  type HoleMap as HoleMapData,
} from '../api/public'
import { HoleMap } from './HoleMap'
import { chosenView, holeYards } from './holeGeometry'
import { MapViewSwitch } from './MapViewSwitch'
import { useApi } from './useApi'
import { useSwipe } from '../hooks/useSwipe'

/**
 * The stroke sheet as a yardage book: each hole of the week drawn before play,
 * with who receives a stroke on it.
 *
 * # Needs a map, and nothing else
 *
 * This is the one place a hole map is useful with no shots at all -- it is read
 * before the round -- so it works from the day a course has a map. A course
 * without one shows nothing here rather than nine empty boxes: the first hole
 * is asked for, and a 404 means the course has no map.
 *
 * # The pin is not known yet
 *
 * A course offers several pin positions and the simulator picks one per round,
 * so before play the map marks the centre of the green rather than guessing.
 */
export function YardageBook({
  event,
  course,
  players,
}: {
  event: EventSummary
  course: CourseValues
  players: GoverningValues[]
}) {
  const holes = playedHoles(event, course)
  const first = holes[0]
  const probe = useApi(
    first ? (s: AbortSignal) => api.courseMapHole(course.course_key, first, s) : null,
    [course.course_key, first],
  )
  const [params] = useSearchParams()
  const { pathname } = useLocation()

  if (!first || probe.state === 'loading') return null
  if (probe.state === 'error') {
    if (probe.error instanceof ApiError && probe.error.status === 404) return null
    return <p className="note">The hole maps could not be loaded.</p>
  }
  const hasAerial = !!probe.data.views.aerial
  const view = chosenView(params.get('view'), hasAerial)
  // The hole shown full screen is in the address, so the back button closes it
  // and the view can be sent to somebody standing on the tee.
  const asked = Number(params.get('hole'))
  const open = holes.includes(asked) ? asked : null
  const holeHref = (n: number | null) => {
    const q = new URLSearchParams(params)
    if (n === null) q.delete('hole')
    else q.set('hole', String(n))
    const s = q.toString()
    return `${pathname}${s ? `?${s}` : ''}`
  }

  return (
    <section className="yardage" data-testid="yardage-book">
      <h3>Holes</h3>
      <p className="note">
        Each hole from the {course.tee_name} tee, with who receives a stroke on it. The pin is
        chosen when the round is set up, so the centre of the green is marked instead.
      </p>
      {hasAerial && <MapViewSwitch view={view} />}
      <ul className="yardage-book">
        {holes.map((n) => (
          <YardageHole
            key={n}
            hole={n}
            course={course}
            players={players}
            view={view}
            openHref={holeHref(n)}
            preloaded={n === first ? probe.data : undefined}
          />
        ))}
      </ul>
      {open !== null && (
        <FullScreenHole
          hole={open}
          course={course}
          players={players}
          view={view}
          closeHref={holeHref(null)}
          prevHref={holes.indexOf(open) > 0 ? holeHref(holes[holes.indexOf(open) - 1]) : null}
          nextHref={
            holes.indexOf(open) < holes.length - 1 ? holeHref(holes[holes.indexOf(open) + 1]) : null
          }
        />
      )}
    </section>
  )
}

function YardageHole({
  hole,
  course,
  players,
  view,
  openHref,
  preloaded,
}: {
  hole: number
  course: CourseValues
  players: GoverningValues[]
  view: 'drawn' | 'aerial'
  openHref: string
  preloaded?: HoleMapData
}) {
  const map = useApi(
    preloaded ? null : (s: AbortSignal) => api.courseMapHole(course.course_key, hole, s),
    [course.course_key, hole, !!preloaded],
  )
  const data = preloaded ?? (map.state === 'ready' ? map.data : null)
  const card = course.card.find((c) => c.hole_number === hole)
  const strokes = strokesOn(hole, players)
  const yards = holeYards(data, course.tee_name)

  return (
    <li className="yardage-hole" data-testid={`yardage-hole-${hole}`}>
      {/* Two fixed lines, never one that wraps: a heading that wraps on one
          card and not its neighbour staggers the whole row of maps. */}
      <h4>Hole {hole}</h4>
      <p className="note yardage-facts">
        {card ? (
          <>
            par {card.par} · index {card.stroke_index}
            {yards !== null && <> · {yards} yd</>}
          </>
        ) : (
          ' '
        )}
      </p>
      {data ? (
        <Link
          to={openHref}
          className="yardage-open"
          aria-label={`Open hole ${hole} full screen`}
          data-testid={`yardage-open-${hole}`}
        >
          <HoleMap
            map={data}
            view={data.views.aerial ? view : 'drawn'}
            tee={course.tee_name}
            label={`Hole ${hole} from the ${course.tee_name} tee`}
            compact
          />
        </Link>
      ) : map.state === 'error' ? (
        <p className="note">No map for this hole.</p>
      ) : (
        <p className="state-loading">Loading…</p>
      )}
      <p className="note">{strokesWords(strokes)}</p>
    </li>
  )
}

/** Who receives a stroke on a hole, from the allocations the sheet already has. */
function strokesOn(hole: number, players: GoverningValues[]) {
  return players
    .map((p) => ({
      name: p.display_name,
      strokes: p.stroke_allocation.find((a) => a.hole === hole)?.strokes ?? '0',
    }))
    .filter((s) => /[1-9]/.test(s.strokes))
}

function strokesWords(strokes: { name: string; strokes: string }[]): string {
  return strokes.length === 0
    ? 'Nobody receives a stroke here.'
    : `Strokes: ${strokes.map((s) => `${s.name} ${s.strokes}`).join(', ')}`
}

/**
 * One hole over the whole screen: what a player looks at while standing on the
 * tee. Closed by its own link, by Escape, or by the back button -- it is a URL,
 * not a control (FR-047c) -- and stepped through with the previous and next
 * links without closing.
 */
function FullScreenHole({
  hole,
  course,
  players,
  view,
  closeHref,
  prevHref,
  nextHref,
}: {
  hole: number
  course: CourseValues
  players: GoverningValues[]
  view: 'drawn' | 'aerial'
  closeHref: string
  prevHref: string | null
  nextHref: string | null
}) {
  const navigate = useNavigate()
  const swipe = useSwipe(
    nextHref ? () => navigate(nextHref, { replace: true }) : null,
    prevHref ? () => navigate(prevHref, { replace: true }) : null,
  )
  const map = useApi(
    (s: AbortSignal) => api.courseMapHole(course.course_key, hole, s),
    [course.course_key, hole],
  )
  const data = map.state === 'ready' ? map.data : null
  const card = course.card.find((c) => c.hole_number === hole)
  const yards = holeYards(data, course.tee_name)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') navigate(closeHref, { replace: true })
      if (e.key === 'ArrowLeft' && prevHref) navigate(prevHref, { replace: true })
      if (e.key === 'ArrowRight' && nextHref) navigate(nextHref, { replace: true })
    }
    window.addEventListener('keydown', onKey)
    // The page behind does not scroll while a hole covers it.
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = overflow
    }
  }, [navigate, closeHref, prevHref, nextHref])

  return (
    <div
      className="hole-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`Hole ${hole}`}
      data-testid="hole-overlay"
      {...swipe}
    >
      <div className="hole-overlay-bar">
        <div>
          <strong>Hole {hole}</strong>
          {card && (
            <span className="hole-overlay-facts">
              {' '}
              · par {card.par} · index {card.stroke_index}
              {yards !== null && <> · {yards} yd</>}
            </span>
          )}
          <div className="hole-overlay-strokes">{strokesWords(strokesOn(hole, players))}</div>
        </div>
        <nav className="hole-overlay-nav" aria-label="Holes">
          {prevHref ? (
            <Link to={prevHref} replace aria-label="Previous hole">
              ←
            </Link>
          ) : (
            <span aria-hidden="true" />
          )}
          {nextHref ? (
            <Link to={nextHref} replace aria-label="Next hole">
              →
            </Link>
          ) : (
            <span aria-hidden="true" />
          )}
          <Link to={closeHref} replace aria-label="Close" data-testid="hole-overlay-close">
            ✕
          </Link>
        </nav>
      </div>
      <div className="hole-overlay-map">
        {data ? (
          <HoleMap
            map={data}
            view={data.views.aerial ? view : 'drawn'}
            tee={course.tee_name}
            label={`Hole ${hole} from the ${course.tee_name} tee`}
          />
        ) : map.state === 'error' ? (
          <p className="note">No map for this hole.</p>
        ) : (
          <p className="state-loading">Loading…</p>
        )}
      </div>
    </div>
  )
}

/** The holes this week plays: a nine-hole week is the front or the back of the card. */
function playedHoles(event: EventSummary, course: CourseValues): number[] {
  const all = course.card.map((c) => c.hole_number).sort((a, b) => a - b)
  if (event.holes_expected === 9 && all.length > 9) {
    return event.track === 'back' ? all.filter((h) => h >= 10) : all.filter((h) => h <= 9)
  }
  return all
}
